import React, { useState } from 'react'
import { useSubscription } from '../../contexts/SubscriptionContext'
import { redirectToCustomerPortal } from '../../lib/stripe-client'
import { supabase } from '../../lib/supabase'
import { AlertCircle, Clock, X } from 'lucide-react'

export const SubscriptionBanner: React.FC = () => {
  const { subscription, hasActiveSubscription } = useSubscription()
  const [isDismissed, setIsDismissed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  if (!hasActiveSubscription || !subscription || isDismissed) {
    return null
  }

  const handleManageSubscription = async () => {
    setIsLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        await redirectToCustomerPortal(session.access_token)
      }
    } catch (error) {
      console.error('Error opening customer portal:', error)
      setIsLoading(false)
    }
  }

  // Calculate days remaining in trial
  const getDaysRemaining = () => {
    if (!subscription.trialEnd) return null
    const now = new Date()
    const trialEnd = new Date(subscription.trialEnd)
    const diffTime = trialEnd.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const daysRemaining = getDaysRemaining()

  // Show trial banner
  if (subscription.isTrialing && daysRemaining !== null && daysRemaining > 0) {
    return (
      <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-b border-blue-600/30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <p className="text-sm text-light-primary">
                <span className="font-semibold">Free Trial: </span>
                {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining until your subscription begins
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleManageSubscription}
                disabled={isLoading}
                className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Loading...' : 'Manage Subscription'}
              </button>
              <button
                onClick={() => setIsDismissed(true)}
                className="text-light-tertiary hover:text-light-primary transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show cancellation warning if subscription is set to cancel
  if (subscription.cancelAtPeriodEnd) {
    const periodEnd = new Date(subscription.currentPeriodEnd)
    const formattedDate = periodEnd.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })

    return (
      <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border-b border-yellow-600/30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <p className="text-sm text-light-primary">
                <span className="font-semibold">Subscription Ending: </span>
                Your access will end on {formattedDate}. Reactivate to continue using BLP Assistant.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleManageSubscription}
                disabled={isLoading}
                className="text-sm bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Loading...' : 'Reactivate Subscription'}
              </button>
              <button
                onClick={() => setIsDismissed(true)}
                className="text-light-tertiary hover:text-light-primary transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // No banner needed for active subscriptions
  return null
}
